/* eslint-disable no-unused-vars */
import {
    createContext,
    type PropsWithChildren,
    useContext,
    useEffect,
    useState,
    useCallback,
    useRef
} from 'react';
import { useLocation } from 'react-router';
import { useMenuChildren } from '@/components/menu';
import { getMenuSidebar } from '@/config/menu.config';
import { useScrollPosition } from '@/hooks/useScrollPosition';
import { useMenus } from '@/providers';
import { ILayoutConfig, useLayout } from '@/providers';
import { deepMerge } from '@/utils';
import { buddyLayoutConfig } from './';

export interface IBuddyLayoutProviderProps {
    layout: ILayoutConfig;
    megaMenuEnabled: boolean;
    headerSticky: boolean;
    mobileSidebarOpen: boolean;
    mobileSidebarChatOpen: boolean;
    mobileMegaMenuOpen: boolean;
    sidebarMouseLeave: boolean;
    sidebarChatMouseLeave: boolean;
    setSidebarMouseLeave: (state: boolean) => void;
    setSidebarChatMouseLeave: (state: boolean) => void;
    setMobileSidebarOpen: (state: boolean) => void;
    setMobileSidebarChatOpen: (state: boolean) => void;
    setMobileMegaMenuOpen: (state: boolean) => void;
    setMegaMenuEnabled: (state: boolean) => void;
    setSidebarCollapse: (state: boolean) => void;
    setSidebarChatCollapse: (state: boolean) => void;
    setSidebarTheme: (theme: string) => void;
    setSidebarChatTheme: (theme: string) => void;
    registerChat: () => void;
    unregisterChat: () => void;
    hasChatOnPage: boolean;
}

const initialLayoutProps: IBuddyLayoutProviderProps = {
    layout: buddyLayoutConfig,
    megaMenuEnabled: false,
    headerSticky: false,
    mobileSidebarOpen: false,
    mobileSidebarChatOpen: false,
    mobileMegaMenuOpen: false,
    sidebarMouseLeave: false,
    sidebarChatMouseLeave: false,
    setSidebarMouseLeave: () => {},
    setSidebarChatMouseLeave: () => {},
    setMobileSidebarOpen: () => {},
    setMobileSidebarChatOpen: () => {},
    setMobileMegaMenuOpen: () => {},
    setMegaMenuEnabled: () => {},
    setSidebarCollapse: () => {},
    setSidebarChatCollapse: () => {},
    setSidebarTheme: () => {},
    setSidebarChatTheme: () => {},
    registerChat: () => {},
    unregisterChat: () => {},
    hasChatOnPage: false,
};

const BuddyLayoutContext = createContext<IBuddyLayoutProviderProps>(initialLayoutProps);
export const useBuddyLayout = () => useContext(BuddyLayoutContext);

export const BuddyLayoutProvider = ({ children }: PropsWithChildren) => {
    const { pathname } = useLocation();
    const { setMenuConfig } = useMenus();
    const secondaryMenu = useMenuChildren(pathname, getMenuSidebar(), 0);

    setMenuConfig('primary', getMenuSidebar());
    setMenuConfig('secondary', secondaryMenu);

    const { getLayout, updateLayout, setCurrentLayout } = useLayout();

    const getLayoutConfig = (): ILayoutConfig => {
        const current = getLayout(buddyLayoutConfig.name);
        const safeCurrent = current ?? {};
        try {
            return deepMerge(buddyLayoutConfig, JSON.parse(JSON.stringify(safeCurrent)));
        } catch (err) {
            console.error('Invalid layout config structure:', err);
            return buddyLayoutConfig;
        }
    };

    const [layout, setLayout] = useState<ILayoutConfig>(getLayoutConfig());

    useEffect(() => {
        const current = getLayout(buddyLayoutConfig.name);
        const currentStr = JSON.stringify(current || {});
        const newStr = JSON.stringify(layout);

        if (currentStr !== newStr) {
            setCurrentLayout(layout);
        }
    }, [layout, getLayout, setCurrentLayout]);

    const [megaMenuEnabled, setMegaMenuEnabled] = useState(false);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [mobileSidebarChatOpen, setMobileSidebarChatOpen] = useState(false);
    const [mobileMegaMenuOpen, setMobileMegaMenuOpen] = useState(false);
    const [sidebarMouseLeave, setSidebarMouseLeave] = useState(false);
    const [sidebarChatMouseLeave, setSidebarChatMouseLeave] = useState(false);

    const scrollPosition = useScrollPosition();
    const headerSticky = scrollPosition > 0;

    const [hasChatOnPage, setHasChatOnPage] = useState(false);
    const lastSidebarChatCollapse = useRef<boolean | undefined>(layout.options.sidebarChat?.collapse);

    const registerChat = useCallback(() => {
        setHasChatOnPage(true);
    }, []);

    const unregisterChat = useCallback(() => {
        setHasChatOnPage(false);
    }, []);

    const setSidebarCollapse = useCallback((collapse: boolean) => {
        updateLayout(buddyLayoutConfig.name, {
            options: { sidebar: { collapse } }
        });
        setLayout(prev => deepMerge(prev, { options: { sidebar: { collapse } } }));
    }, [updateLayout]);

    const setSidebarChatCollapse = useCallback((collapse: boolean) => {
        lastSidebarChatCollapse.current = collapse;
        updateLayout(buddyLayoutConfig.name, {
            options: { sidebarChat: { collapse } }
        });
        setLayout(prev => deepMerge(prev, { options: { sidebarChat: { collapse } } }));
    }, [updateLayout]);

    useEffect(() => {
        if (!hasChatOnPage && layout.options.sidebarChat.collapse !== true) {
            lastSidebarChatCollapse.current = true;
            setSidebarChatCollapse(true);
        }
    }, [hasChatOnPage, layout.options.sidebarChat.collapse, setSidebarChatCollapse]);

    const setSidebarTheme = useCallback((theme: string) => {
        setLayout(prev => deepMerge(prev, { options: { sidebar: { theme } } }));
    }, []);

    const setSidebarChatTheme = useCallback((theme: string) => {
        setLayout(prev => deepMerge(prev, { options: { sidebarChat: { theme } } }));
    }, []);

    return (
        <BuddyLayoutContext.Provider
            value={{
                layout,
                headerSticky,
                mobileSidebarOpen,
                mobileSidebarChatOpen,
                mobileMegaMenuOpen,
                megaMenuEnabled,
                sidebarMouseLeave,
                sidebarChatMouseLeave,
                setMobileSidebarOpen,
                setMobileSidebarChatOpen,
                setMobileMegaMenuOpen,
                setMegaMenuEnabled,
                setSidebarMouseLeave,
                setSidebarChatMouseLeave,
                setSidebarCollapse,
                setSidebarChatCollapse,
                setSidebarTheme,
                setSidebarChatTheme,
                registerChat,
                unregisterChat,
                hasChatOnPage
            }}
        >
            {children}
        </BuddyLayoutContext.Provider>
    );
};
